import {
  Card,
  IconWrapper,
  Link,
  SegmentedControl,
} from "@caprikorn/dynamo-ui";
import { useIsFetching } from "@tanstack/react-query";
import { type FC, useState } from "react";

import { Header } from "@components/Header/Header";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { useGroupsDropdown } from "@hooks/useGroupsDropdown";
import { House01 } from "@icons";
import { ROUTES } from "@router/router.constants";
import { QUERY_KEYS } from "@services/keys";
import type { Energy } from "@services/services.types";
import { CustomTrans } from "@utils/translations/CustomTrans";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./DashBoard.module.css";
import { DashboardContent } from "./DashboardContent/DashboardContent";

export const Dashboard: FC = () => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation(["main", "common"]);
  const { authorizedEnergyType } = useTenant();
  const [energyType, setEnergyType] = useState<Energy>(
    authorizedEnergyType === "gas" ? "GAS" : "ELEC",
  );

  const dashboardKeyCheckFetching = [
    QUERY_KEYS.CONSUMPTION_GRAPH,
    energyType,
    currentGroup,
  ];
  const isFetchingPosts = useIsFetching({
    queryKey: dashboardKeyCheckFetching,
  });

  const { groupsDropdownOptions, groupsOnDropdownChange, groupsDropdownValue } =
    useGroupsDropdown();

  return (
    <>
      <Header
        breadcrumbPaths={[
          {
            pathName: t("dashboard.breadcrumbTitle"),
            path: ROUTES.DASHBOARD,
            pathIcon: House01,
          },
        ]}
        dropdownOptions={groupsDropdownOptions}
        dropdownValue={groupsDropdownValue}
        onDropdownChange={(select) => groupsOnDropdownChange(select)}
      />
      <div className={classes.container}>
        <h1 className="font-title-lg">{t("dashboard.title")}</h1>
        {authorizedEnergyType === "dual" && (
          <SegmentedControl
            className={classes.segmentedControl}
            data-testid="Toggle-container"
            disabled={isFetchingPosts !== 0}
            onChange={(e) => setEnergyType(e as Energy)}
          >
            <SegmentedControl.Item
              data-testid="Toggle-Electricity"
              isSelected={energyType === "ELEC"}
              label={t("energy.ELEC", { ns: "common" })}
              value="ELEC"
            />
            <SegmentedControl.Item
              data-testid="Toggle-Gas"
              isSelected={energyType === "GAS"}
              label={t("energy.GAS", { ns: "common" })}
              value="GAS"
            />
          </SegmentedControl>
        )}
        <Card className={classes.helpBanner} role="alert">
          <IconWrapper className={classes.icon} size="2xl" src="light" />
          <CustomTrans
            components={{
              a: <Link href={ROUTES.PERIMETER_CUSTOMERS} />,
            }}
            i18nKey="dashboard.helpBanner"
            ns="main"
            parent="p"
          />
        </Card>
        <DashboardContent energyType={energyType} />
      </div>
    </>
  );
};
